import { useEffect, useState } from "react";
import { renderDateViewCalendar, type DateViewRendererProps } from "@mui/x-date-pickers/dateViewRenderers";
import type { DateView } from "@mui/x-date-pickers";

/**
 * Drives a month/year CoreDatePicker from a committed month.
 *
 * MUI calls `onAccept` on Escape/click-away and `onMonthChange` when navigating years.
 * Commit only a finished calendar selection, and restore the committed month on dismiss.
 */
export function useMonthPicker(month: Date, commit: (date: Date) => void) {
	const [draft, setDraft] = useState(month);

	useEffect(() => setDraft(month), [month]);
	const renderCalendar = (props: DateViewRendererProps<Date, DateView>) =>
		renderDateViewCalendar<Date>({
			...props,
			onChange: (date, selectionState, selectedView) => {
				props.onChange?.(date, selectionState, selectedView);
				if (date && selectionState === "finish") commit(date);
			},
		});

	return {
		value: draft,
		onChange: (date: Date | null) => date && setDraft(date),
		viewRenderers: { year: renderCalendar, month: renderCalendar },
		onClose: () => setDraft(month),
	};
}
